import {
  type Bounds,
  centerOffsetY,
  containsPoint,
  destination,
  displayColor,
  distanceM,
  followOffsetY,
  LEADER_COLOR,
  MAP_FOLLOW,
  navigationOffsetY,
  type Participant,
  PLANNED_ROUTE_COLOR,
  ROUTE_COLOR,
  STALENESS,
  type TrackPoint,
  type VehicleState,
  type Waypoint,
  WAYPOINT_KIND_NAMES,
} from '@konvoi/shared';

import {
  annaeherungsanteil,
  kameraSchritt,
  kartenZoom,
  type Kamerastand,
} from '../lib/kartenfuehrung.js';
import {
  addProtocol,
  GeoJSONSource,
  LngLatBounds,
  Map as MapLibreMap,
  Marker,
} from 'maplibre-gl';
import { PMTiles, Protocol } from 'pmtiles';
import { useEffect, useRef, useState } from 'react';

import { initials } from '../lib/labels.js';
import { setzeOderLegeAn } from './bereit.js';
import { buildStyle, TILE_URL } from '@konvoi/karte';
import { CachingPMTilesSource, pruneTileCache } from './tileCache.js';
import 'maplibre-gl/dist/maplibre-gl.css';

/**
 * Die Karte.
 *
 * Zwei Dinge unterscheiden sie von einer gewöhnlichen Kartendarstellung:
 *
 *   * **Die Fahrzeuge gleiten, sie springen nicht.** Zwischen zwei Meldungen
 *     wird die Bewegung anhand von Kurs und Geschwindigkeit fortgeschrieben und
 *     weich nachgeführt. Ein Sprung im Sekundentakt ist auf einer Karte im Auto
 *     kaum lesbar — und wirkt zudem hektischer, als die Lage ist.
 *   * **Die Fortschreibung endet.** Nach wenigen Sekunden ohne neue Meldung
 *     wird nicht weiter geraten, sondern angehalten und gekennzeichnet. Eine
 *     veraltete Position darf niemals wie eine aktuelle aussehen.
 */

export type MapOrientation = 'north' | 'course';
/**
 * Wonach sich der Kartenausschnitt richtet.
 *
 * `self` das eigene Fahrzeug, `all` die ganze Kolonne, `leader` das
 * Führungsfahrzeug — und `free` niemand: Wer die Karte selbst verschoben hat,
 * behält sie.
 */
export type FollowMode = 'self' | 'all' | 'leader' | 'free' | 'navigation';

export interface ConvoyMapProps {
  vehicles: readonly VehicleState[];
  participants: readonly Participant[];
  /**
   * Wann zuletzt eine **neue** Meldung eines Fahrzeugs eintraf, nach der
   * eigenen Uhr. Grundlage dafür, wann die Fortschreibung aufhört.
   */
  lastReportAt: ReadonlyMap<string, number>;
  youId: string | null;
  track: ReadonlyMap<string, readonly TrackPoint[]>;
  /**
   * Die laufende Navigation zur Kolonne — Stufe 2. `null` heißt: keine.
   *
   * Sie liegt über der Führungsspur, in einem deutlich anderen Ton: Wo beide
   * übereinanderliegen, muss erkennbar bleiben, welche Linie den Weg zeigt.
   */
  route: readonly { lat: number; lon: number }[] | null;
  /**
   * Die **geplante** Route der Ausfahrt — Stufe 3. `null` heißt: nichts geplant.
   *
   * Drei Linien liegen jetzt übereinander, und sie sagen Verschiedenes: Die
   * orange Führungsspur ist, wo die Kolonne wirklich gefahren ist; die blaue
   * Navigation ist der Weg zurück; diese ist, was jemand sich vorgenommen hat.
   * Der Plan ist die schwächste Aussage von dreien und liegt deshalb **unter**
   * den anderen beiden — dünner und gestrichelt, damit die Unterscheidung
   * nicht allein an der Farbe hängt (Abschnitt 2).
   */
  plannedRoute: readonly { lat: number; lon: number }[] | null;
  /**
   * Die Treffpunkte der Ausfahrt. Stützpunkte sind nicht dabei — der Server
   * lässt sie weg, weil dort niemand hält.
   */
  waypoints: readonly Waypoint[];
  /** Welcher davon als nächster kommt. Nur er trägt seinen Namen auf der Karte. */
  nextWaypointId: string | null;
  orientation: MapOrientation;
  /**
   * Wie viele Pixel am unteren Rand von Rückstandsfläche und Bedienung verdeckt
   * sind.
   *
   * Die Karte reicht über den ganzen Bildschirm, sichtbar ist sie nur oben — und
   * wie viel, ändert sich: Bei laufender Navigation steht unten eine Reihe mehr.
   * Nur mit dieser Zahl lässt sich das eigene Fahrzeug verlässlich über der
   * Bedienung halten (A-80).
   */
  bottomOverlayPx: number;
  follow: FollowMode;
  /**
   * Das zuletzt gemessene eigene Tempo, in Metern je Sekunde.
   *
   * Nur für die Navigationsansicht: Dort bestimmt es die Vergrößerung. In den
   * übrigen Ansichten entscheidet der Abstand zu den anderen, was zu sehen ist.
   */
  ownSpeedMps: number | null;
  dark: boolean;
  /** Der Nutzer hat die Karte selbst verschoben. */
  onUserPan: () => void;
  /**
   * Die Karte konnte nicht geladen werden.
   *
   * Auf einer Ausfahrt ist das ein wichtiger Zustand und keine Randnotiz: Ohne
   * Karte bleibt die Kolonne zwar sichtbar, aber ohne Umgebung. Der Fahrer muss
   * erfahren, dass es an der Karte liegt und nicht an den anderen.
   */
  onMapError: (message: string) => void;
}

interface Rendered {
  lat: number;
  lon: number;
  marker: Marker;
  element: HTMLElement;
}

/** Einmalige Anmeldung des PMTiles-Protokolls an MapLibre. */
let protocolRegistered = false;
/** Die Kartendatei selbst — auch die Quelle für ihre Ausdehnung. */
let kartendatei: PMTiles | null = null;

function registerProtocol(): void {
  if (protocolRegistered) {
    return;
  }
  const protocol = new Protocol();
  kartendatei = new PMTiles(new CachingPMTilesSource(TILE_URL));
  protocol.add(kartendatei);
  addProtocol('pmtiles', protocol.tile);
  protocolRegistered = true;

  // Einmal aufräumen: Bereiche eines früheren Kartengebiets sind unerreichbar
  // geworden und lägen sonst für immer auf dem Gerät.
  void pruneTileCache(TILE_URL);
}

/**
 * Das Gebiet, für das Kartenmaterial vorliegt.
 *
 * Steht im Kopf der Kartendatei und wird von dort gelesen — nicht neben ihr
 * noch einmal aufgeschrieben. Sonst stimmte die Angabe genau so lange, bis
 * jemand das Gebiet neu erzeugt und die zweite Stelle vergisst.
 *
 * Der Kopf ist ohnehin das Erste, was der Client von der Datei liest; die
 * Abfrage kostet keine zusätzliche Übertragung.
 */
async function kartengebiet(): Promise<Bounds | null> {
  if (kartendatei === null) {
    return null;
  }
  const kopf = await kartendatei.getHeader();
  return {
    minLat: kopf.minLat,
    minLon: kopf.minLon,
    maxLat: kopf.maxLat,
    maxLon: kopf.maxLon,
  };
}

export function ConvoyMap(props: ConvoyMapProps): React.JSX.Element {
  const container = useRef<HTMLDivElement | null>(null);
  const map = useRef<MapLibreMap | null>(null);
  const markers = useRef(new Map<string, Rendered>());
  /** Die Nadeln der Treffpunkte, nach ihrer Kennung. */
  const treffpunkte = useRef(new Map<string, Marker>());
  const latest = useRef(props);
  latest.current = props;

  /*
   * Liegt gerade ein Finger auf der Karte?
   *
   * Solange das so ist, führt die App die Karte nicht nach. Sonst arbeitet die
   * Bildschleife gegen den Finger: Sie schiebt den Ausschnitt sechzig Mal je
   * Sekunde dorthin zurück, wo er ihrer Meinung nach hingehört — und Ziehen
   * wie Aufziehen greifen nicht oder erst beim zweiten, dritten Versuch.
   *
   * Bewusst ein ref und kein Zustand: Der Merker muss in demselben Augenblick
   * gelten, in dem die Karte berührt wird. Auf ein erneutes Zeichnen von React
   * zu warten, wären mehrere Bilder, in denen die Karte weiter dagegenhält.
   */
  const berührt = useRef(false);

  /** Wann der Ausschnitt zuletzt an die ganze Kolonne angepasst wurde. */
  const letzteAnpassung = useRef(0);
  /** Die zuletzt gesetzte Vergrößerung der Navigationsansicht — für die Rückschau. */
  const letzterZoom = useRef<number | null>(null);
  /**
   * Vergrößerung und Ausrichtung, wie sie **gerade dargestellt** werden.
   *
   * Nicht dasselbe wie `letzterZoom`: Der hält das **Ziel** fest, damit die
   * Rückschau nicht bei jedem Tempowackler eine neue Stufe wählt. Hier steht
   * der Wert, der sich diesem Ziel Bild für Bild nähert.
   *
   * Seit dem 03.09.2026 glättet die Ansicht „mein Fahrzeug" selbst, statt die
   * Kamera von MapLibre glätten zu lassen — siehe `folgeDemFahrzeug`.
   */
  const kamera = useRef<Kamerastand>({ zoom: null, bearing: null });

  /**
   * Steht der Ausschnitt außerhalb des Kartengebiets?
   *
   * Dann bleibt die Fläche leer. Ohne ein Wort dazu sieht das aus wie ein
   * Fehler — am 14.08.2026 an der niederländischen Küste genau so erlebt.
   */
  const [ausserhalb, setAusserhalb] = useState(false);
  const gebiet = useRef<Bounds | null>(null);

  // ------------------------------------------------------------------ Karte
  useEffect(() => {
    if (container.current === null) {
      return;
    }

    registerProtocol();

    const karte = new MapLibreMap({
      container: container.current,
      style: buildStyle(props.dark),
      center: [8.7464, 51.7116],
      zoom: 12,
      attributionControl: { compact: true },
      // Karten im Auto werden nicht gedreht und nicht gekippt; beides wäre bei
      // einem versehentlichen Zweifingertipp nur verwirrend.
      pitchWithRotate: false,
      dragRotate: false,
      touchZoomRotate: true,
    });

    karte.touchZoomRotate.disableRotation();

    karte.on('error', (ereignis) => {
      const meldung = ereignis.error?.message ?? 'unbekannter Fehler';
      // Vollständig ins Protokoll, verständlich an den Fahrer.
      console.error('Karte:', meldung, ereignis.error);
      latest.current.onMapError(
        `Die Karte lässt sich nicht laden (${meldung}). Die Kolonne wird trotzdem angezeigt.`,
      );
    });
    /*
     * Eine tatsächliche Bewegung gibt die Karte dauerhaft frei — Ziehen,
     * Aufziehen, Mausrad, Doppeltipp. Ein bloßes Antippen nicht: Im Auto wird
     * die Karte auch einmal versehentlich gestreift, und dann darf die
     * Nachführung nicht stillschweigend abreißen.
     *
     * `originalEvent` unterscheidet den Finger von der eigenen Kameraführung.
     * Ohne diese Prüfung gäbe sich die Karte bei jeder Nachführung selbst frei
     * und stünde nach dem ersten Bild still.
     */
    const beiEigenerBewegung = (ereignis: { originalEvent?: unknown }): void => {
      if (ereignis.originalEvent === undefined) {
        return;
      }
      latest.current.onUserPan();
    };
    karte.on('dragstart', beiEigenerBewegung);
    karte.on('zoomstart', beiEigenerBewegung);
    karte.on('rotatestart', beiEigenerBewegung);

    // Der Merker wird an der Zeichenfläche selbst gesetzt, nicht über die
    // Ereignisse von MapLibre: Die melden eine Bewegung erst, wenn der Finger
    // eine Mindeststrecke zurückgelegt hat. Bis dahin dürfte die App längst
    // nicht mehr mitsteuern.
    const flaeche = karte.getCanvasContainer();
    const beiBeruehrung = (): void => {
      berührt.current = true;
    };
    const beiLoslassen = (): void => {
      berührt.current = false;
    };
    flaeche.addEventListener('touchstart', beiBeruehrung, { passive: true });
    flaeche.addEventListener('mousedown', beiBeruehrung);
    // Losgelassen wird am Fenster, nicht an der Karte: Ein Finger, der neben
    // der Karte endet, würde den Merker sonst gesetzt lassen — und die
    // Nachführung bliebe für den Rest der Ausfahrt aus.
    window.addEventListener('touchend', beiLoslassen, { passive: true });
    window.addEventListener('touchcancel', beiLoslassen, { passive: true });
    window.addEventListener('mouseup', beiLoslassen);

    /*
     * Nach jeder Bewegung: Liegt die Mitte des Ausschnitts noch im
     * Kartengebiet? Die Mitte und nicht die eigene Position — beim Verschieben
     * schaut der Fahrer dorthin, und leer ist die Fläche dort.
     */
    const pruefeGebiet = (): void => {
      const grenzen = gebiet.current;
      if (grenzen === null) {
        return;
      }
      const mitte = karte.getCenter();
      setAusserhalb(!containsPoint(grenzen, { lat: mitte.lat, lon: mitte.lng }));
    };
    karte.on('moveend', pruefeGebiet);

    void kartengebiet()
      .then((grenzen) => {
        gebiet.current = grenzen;
        pruefeGebiet();
      })
      .catch((fehler: unknown) => {
        // Kein Grund für eine Meldung an den Fahrer: Ohne die Ausdehnung
        // entfällt nur der Hinweis, die Karte selbst läuft weiter.
        console.error('Kartengebiet nicht lesbar:', fehler);
      });

    map.current = karte;

    return () => {
      flaeche.removeEventListener('touchstart', beiBeruehrung);
      flaeche.removeEventListener('mousedown', beiBeruehrung);
      window.removeEventListener('touchend', beiLoslassen);
      window.removeEventListener('touchcancel', beiLoslassen);
      window.removeEventListener('mouseup', beiLoslassen);
      for (const eintrag of markers.current.values()) {
        eintrag.marker.remove();
      }
      markers.current.clear();
      for (const nadel of treffpunkte.current.values()) {
        nadel.remove();
      }
      treffpunkte.current.clear();
      karte.remove();
      map.current = null;
    };
    // Der Stil wird unten gewechselt, nicht durch Neuaufbau der Karte.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Wechsel zwischen heller und dunkler Karte.
  useEffect(() => {
    map.current?.setStyle(buildStyle(props.dark));
  }, [props.dark]);

  // ---------------------------------------------------- Die geplante Route
  //
  // Steht **vor** der Führungsspur, und das ist kein Zufall: MapLibre legt
  // Ebenen in der Reihenfolge ihres Anlegens übereinander. Wer den Plan zuerst
  // anlegt, bekommt ihn unter die gefahrene Spur — und genau dort gehört er
  // hin. Zur Sicherheit nennt `addLayer` zusätzlich die Ebene, vor die sie
  // gehört, falls die Reihenfolge der Effekte sich einmal ändert.
  useEffect(() => {
    const karte = map.current;
    if (karte === null) {
      return;
    }

    const zeichnen = (): void => {
      const punkte = props.plannedRoute ?? [];
      const daten = {
        type: 'FeatureCollection' as const,
        features:
          punkte.length < 2
            ? []
            : [
                {
                  type: 'Feature' as const,
                  id: 'geplante-route',
                  properties: {},
                  geometry: {
                    type: 'LineString' as const,
                    coordinates: punkte.map((punkt) => [punkt.lon, punkt.lat]),
                  },
                },
              ],
      };

      const quelle = karte.getSource('geplante-route');
      if (quelle === undefined) {
        karte.addSource('geplante-route', { type: 'geojson', data: daten });
        karte.addLayer(
          {
            id: 'geplante-route-linie',
            type: 'line',
            source: 'geplante-route',
            layout: { 'line-cap': 'butt', 'line-join': 'round' },
            paint: {
              'line-color': PLANNED_ROUTE_COLOR.hex,
              // Dünner als die Führungsspur: Der Plan tritt hinter das zurück,
              // was wirklich gefahren wurde.
              'line-width': ['interpolate', ['linear'], ['zoom'], 10, 2, 14, 4, 17, 6],
              // Striche und Lücken in Vielfachen der Linienbreite. Bei jeder
              // Zoomstufe bleibt das Muster damit erkennbar.
              'line-dasharray': [2, 1.5],
              'line-opacity': 0.85,
            },
          },
          karte.getLayer('fuehrungsspur-linie') === undefined ? undefined : 'fuehrungsspur-linie',
        );
      } else if (quelle instanceof GeoJSONSource) {
        quelle.setData(daten);
      }
    };

    return setzeOderLegeAn(
      karte,
      () => {
        if (karte.getSource('geplante-route') === undefined) {
          return false;
        }
        zeichnen();
        return true;
      },
      zeichnen,
    );
  }, [props.plannedRoute]);

  // ---------------------------------------------------------- Treffpunkte
  /*
   * Eine **dritte Form**: Rauten sind die Führung, Kreise die Teilnehmer — ein
   * Treffpunkt ist keins von beidem und darf nicht so aussehen. Die Nadel ist
   * das Kartenzeichen, das jeder kennt, und sie zeigt nach unten auf ihren Ort
   * statt ihn zu verdecken.
   *
   * **Alle Arten dieselbe Form.** Der Entwurf lässt mehrere unterscheidbare zu,
   * mahnt aber: „Die Kartenfläche ist knapp; jedes zusätzliche Zeichen will
   * verdient sein." Bei 60 km/h ist die Frage nicht, ob dort gegessen oder
   * gesammelt wird, sondern **dass** dort etwas ist. Welche Art und wie er
   * heißt, steht in der Leiste.
   *
   * **Und kein Name an der Nadel.** Er stand dort bis zum 15.09.2026 und war
   * zweimal falsch: Er sagte dasselbe wie die Leiste, und er war nie zu sehen.
   * Der nächste Treffpunkt liegt voraus, voraus ist oben — und oben liegt die
   * Leiste. Gemessen bei 94 m Entfernung: Nadel bei y 109 bis 165, Leiste bei
   * y 108 bis 174. Die Nadel lag vollständig dahinter.
   *
   * Der nächste Punkt ist trotzdem erkennbar: größer und mit einem Ring. Das
   * ist Form, nicht Farbe.
   */
  useEffect(() => {
    const karte = map.current;
    if (karte === null) {
      return;
    }

    const uebrig = new Set(treffpunkte.current.keys());

    for (const punkt of props.waypoints) {
      uebrig.delete(punkt.id);
      const naechster = punkt.id === props.nextWaypointId;
      let marker = treffpunkte.current.get(punkt.id);

      if (marker === undefined) {
        const element = document.createElement('div');
        element.className = 'treffpunktnadel';
        element.innerHTML = '<span class="treffpunktnadel__kopf"></span>';
        marker = new Marker({ element, anchor: 'bottom' })
          .setLngLat([punkt.lon, punkt.lat])
          .addTo(karte);
        treffpunkte.current.set(punkt.id, marker);
      } else {
        marker.setLngLat([punkt.lon, punkt.lat]);
      }

      const element = marker.getElement();
      element.classList.toggle('treffpunktnadel--naechster', naechster);
      const kopf = element.querySelector<HTMLElement>('.treffpunktnadel__kopf');
      if (kopf !== null) {
        // Aus der einen Wahrheit, nicht aus einem zweiten Ton im Stilblatt: Die
        // Nadel gehört zum Plan und trägt dessen Farbe.
        kopf.style.backgroundColor = PLANNED_ROUTE_COLOR.hex;
      }
      element.title = `${WAYPOINT_KIND_NAMES[punkt.kind]}: ${punkt.name}`;
      element.setAttribute(
        'aria-label',
        `${WAYPOINT_KIND_NAMES[punkt.kind]}: ${punkt.name}${naechster ? ', der nächste' : ''}`,
      );
    }

    for (const id of uebrig) {
      treffpunkte.current.get(id)?.remove();
      treffpunkte.current.delete(id);
    }
  }, [props.waypoints, props.nextWaypointId]);

  // ------------------------------------------------------- Spur der Führung
  useEffect(() => {
    const karte = map.current;
    if (karte === null) {
      return;
    }

    const zeichnen = (): void => {
      const linien = [...props.track.entries()]
        .filter(([, punkte]) => punkte.length >= 2)
        .map(([segmentId, punkte]) => ({
          type: 'Feature' as const,
          id: segmentId,
          properties: {},
          geometry: {
            type: 'LineString' as const,
            coordinates: punkte.map((punkt) => [punkt.lon, punkt.lat]),
          },
        }));

      const daten = { type: 'FeatureCollection' as const, features: linien };
      const quelle = karte.getSource('fuehrungsspur');

      if (quelle === undefined) {
        karte.addSource('fuehrungsspur', { type: 'geojson', data: daten });
        karte.addLayer({
          id: 'fuehrungsspur-linie',
          type: 'line',
          source: 'fuehrungsspur',
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: {
            // Dieselbe Farbe wie das Führungsfahrzeug: Spitze und Spur
            // erklären sich damit gegenseitig.
            'line-color': LEADER_COLOR.hex,
            // Kräftig genug, um bei Sonne und in Bewegung erkennbar zu bleiben.
            'line-width': ['interpolate', ['linear'], ['zoom'], 10, 4, 14, 7, 17, 10],
            'line-opacity': 0.9,
          },
        });
      } else if (quelle instanceof GeoJSONSource) {
        quelle.setData(daten);
      }
    };

    /*
     * Gibt es die Quelle schon, wird sofort gezeichnet — auch wenn gerade
     * Kacheln laden. Nur das erste Anlegen wartet, bis der Stil es annimmt
     * (A-109). `zeichnen` legt an, wo die Quelle fehlt, und aktualisiert sonst;
     * die Hülle sagt `setzeOderLegeAn`, welcher Fall vorliegt.
     */
    return setzeOderLegeAn(
      karte,
      () => {
        if (karte.getSource('fuehrungsspur') === undefined) {
          return false;
        }
        zeichnen();
        return true;
      },
      zeichnen,
    );
  }, [props.track, props.dark]);

  // ------------------------------------------------------- Navigation
  useEffect(() => {
    const karte = map.current;
    if (karte === null) {
      return;
    }

    const zeichnen = (): void => {
      const punkte = props.route ?? [];
      const daten = {
        type: 'FeatureCollection' as const,
        features:
          punkte.length < 2
            ? []
            : [
                {
                  type: 'Feature' as const,
                  id: 'navigation',
                  properties: {},
                  geometry: {
                    type: 'LineString' as const,
                    coordinates: punkte.map((punkt) => [punkt.lon, punkt.lat]),
                  },
                },
              ],
      };

      const quelle = karte.getSource('navigation');

      if (quelle === undefined) {
        karte.addSource('navigation', { type: 'geojson', data: daten });
        /*
         * Zwei Linien übereinander: eine breite helle darunter, die farbige
         * darüber. Ohne diese Fassung verschwindet ein blauer Strich bei Sonne
         * in einer Straße, die selbst schon dunkel gezeichnet ist.
         */
        karte.addLayer({
          id: 'navigation-fassung',
          type: 'line',
          source: 'navigation',
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: {
            'line-color': props.dark ? '#000000' : '#ffffff',
            'line-width': ['interpolate', ['linear'], ['zoom'], 10, 9, 14, 13, 17, 18],
            'line-opacity': 0.7,
          },
        });
        karte.addLayer({
          id: 'navigation-linie',
          type: 'line',
          source: 'navigation',
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: {
            'line-color': ROUTE_COLOR.hex,
            'line-width': ['interpolate', ['linear'], ['zoom'], 10, 5, 14, 8, 17, 12],
          },
        });
      } else if (quelle instanceof GeoJSONSource) {
        quelle.setData(daten);
      }
    };

    /*
     * Hier lag die hängengebliebene blaue Linie (A-104, A-109).
     *
     * Endet die Navigation, läuft dieser Effekt genau einmal mit `route: null`.
     * Lud in diesem Augenblick eine Kachel, war `isStyleLoaded()` falsch, und
     * das Abräumen wartete auf `styledata` — das `setData` nie auslöst. Die
     * letzte Route blieb stehen. Jetzt wird eine vorhandene Quelle sofort
     * geleert; warten muss nur das allererste Anlegen.
     *
     * Der Rückgabewert bleibt die Aufräumfunktion: Ein Hörer aus einem vorigen
     * Bild darf nie auslösen.
     */
    return setzeOderLegeAn(
      karte,
      () => {
        if (karte.getSource('navigation') === undefined) {
          return false;
        }
        zeichnen();
        return true;
      },
      zeichnen,
    );
  }, [props.route, props.dark]);

  // ------------------------------------------------------------- Fahrzeuge
  useEffect(() => {
    const karte = map.current;
    if (karte === null) {
      return;
    }

    const gesehen = new Set<string>();

    for (const fahrzeug of props.vehicles) {
      const teilnehmer = props.participants.find((p) => p.id === fahrzeug.participantId);
      if (teilnehmer === undefined) {
        continue;
      }
      gesehen.add(fahrzeug.participantId);

      let eintrag = markers.current.get(fahrzeug.participantId);

      if (eintrag === undefined) {
        const element = document.createElement('div');
        element.className = 'kartenpunkt';
        /*
         * Zwei Ebenen, und das ist kein Beiwerk: Den äußeren Knoten führt
         * MapLibre — die Karte setzt dort ihr eigenes `transform`, um den
         * Marker zu setzen. Form, Farbe und Drehung liegen deshalb im inneren.
         * Standen sie außen, überschrieb die Karte die Raute der Führung.
         */
        element.innerHTML =
          '<div class="kartenpunkt__flaeche"><span class="kartenpunkt__kuerzel"></span></div>';
        const marker = new Marker({ element, anchor: 'center' })
          .setLngLat([fahrzeug.lon, fahrzeug.lat])
          .addTo(karte);
        eintrag = { lat: fahrzeug.lat, lon: fahrzeug.lon, marker, element };
        markers.current.set(fahrzeug.participantId, eintrag);
      }

      const kürzel = eintrag.element.querySelector('.kartenpunkt__kuerzel');
      if (kürzel !== null) {
        kürzel.textContent = initials(teilnehmer.displayName);
      }

      // Rolle vor Person: Führung und Schlusswagen tragen ihre feste Farbe und
      // eine eigene Form, alle übrigen ihre persönliche Farbe im Kreis.
      const flaeche = eintrag.element.querySelector<HTMLElement>('.kartenpunkt__flaeche');
      if (flaeche !== null) {
        // Nur die Farbe, nicht die Kurzschreibweise `background`: Die setzte auch
        // das Hintergrundbild zurück — und damit den Schleier, der ein ruhendes
        // Fahrzeug blass macht.
        flaeche.style.backgroundColor = displayColor(teilnehmer.role, teilnehmer.color);
      }
      eintrag.element.classList.toggle('kartenpunkt--fuehrung', teilnehmer.role === 'leader');
      eintrag.element.classList.toggle('kartenpunkt--schluss', teilnehmer.role === 'sweeper');
      eintrag.element.classList.toggle('kartenpunkt--ich', teilnehmer.id === props.youId);
      eintrag.element.classList.toggle(
        'kartenpunkt--ruhend',
        fahrzeug.convoyState === 'paused' || fahrzeug.convoyState === 'no_signal',
      );
      // Das Kennzeichen zuerst: Danach sucht man, wenn man wissen will, wer da
      // vor einem fährt.
      eintrag.element.title = [teilnehmer.displayName, teilnehmer.licensePlate, teilnehmer.vehicleLabel]
        .filter((teil) => teil !== null && teil !== '')
        .join(' — ');
    }

    for (const [id, eintrag] of markers.current) {
      if (!gesehen.has(id)) {
        eintrag.marker.remove();
        markers.current.delete(id);
      }
    }
  }, [props.vehicles, props.participants, props.youId]);

  // --------------------------------------------- Weiche Bewegung, Bild für Bild
  useEffect(() => {
    let laufend = 0;

    /** Zeitpunkt des vorigen Bildes — Grundlage der zeitbasierten Annäherung. */
    let vorigesBild = performance.now();

    const schritt = (): void => {
      const karte = map.current;
      const aktuell = latest.current;

      const jetztBild = performance.now();
      // Nach einer Pause liegt zwischen zwei Bildern beliebig viel Zeit. Die
      // Begrenzung hält den Anteil in einem sinnvollen Bereich; den großen
      // Sprung erledigt ohnehin die Prüfung weiter unten.
      const abstandMs = Math.min(jetztBild - vorigesBild, 1_000);
      vorigesBild = jetztBild;

      if (karte !== null) {
        for (const fahrzeug of aktuell.vehicles) {
          const eintrag = markers.current.get(fahrzeug.participantId);
          if (eintrag === undefined) {
            continue;
          }

          /*
           * Wie lange ist die Meldung her?
           *
           * Gemessen an der eigenen Uhr, damit eine falsch gehende Uhr im
           * fremden Gerät nichts verzerrt — und am Zeitpunkt der letzten
           * **neuen** Meldung dieses Fahrzeugs.
           *
           * Vorher stand hier ein Zeitstempel, der bei jeder Sammelmeldung
           * neu gesetzt wurde. Die kommt jede Sekunde für alle Fahrzeuge, auch
           * für längst verstummte — die Abbruchbedingung darunter griff damit
           * nie, und die Karte schrieb ein Fahrzeug ohne Meldung endlos weiter
           * fort. Genau das, was sie nicht tun soll.
           */
          const gemeldet = aktuell.lastReportAt.get(fahrzeug.participantId);
          const seitMeldung = gemeldet === undefined ? Infinity : Date.now() - gemeldet;

          // Ziel: die gemeldete Position, um die seither vermutlich gefahrene
          // Strecke fortgeschrieben. Nach wenigen Sekunden ohne neue Meldung
          // wird nicht weiter geraten.
          const fortschreiben =
            seitMeldung < STALENESS.extrapolationStopMs &&
            fahrzeug.speedMps !== null &&
            fahrzeug.headingDeg !== null &&
            fahrzeug.speedMps > 0.5;

          const ziel = fortschreiben
            ? destination(
                { lat: fahrzeug.lat, lon: fahrzeug.lon },
                fahrzeug.headingDeg ?? 0,
                (fahrzeug.speedMps ?? 0) * (seitMeldung / 1000),
              )
            : { lat: fahrzeug.lat, lon: fahrzeug.lon };

          /*
           * Weiche Annäherung — aber nicht über jede Entfernung.
           *
           * Wer aus dem Hintergrund zurückkommt oder aus einem Funkloch
           * nachsendet, springt um Kilometer. Diese Strecke ist er in diesem
           * Augenblick nicht gefahren; sie zu zeigen hieße, eine Fahrt
           * darzustellen, die nie stattgefunden hat. Also wird gesprungen.
           */
          if (distanceM(eintrag, ziel) > MAP_FOLLOW.jumpDistanceM) {
            eintrag.lat = ziel.lat;
            eintrag.lon = ziel.lon;
          } else {
            /*
             * Der Anteil hängt an der **Zeit**, nicht an der Bildrate.
             *
             * Vorher rückte jedes Fahrzeug je Einzelbild um feste 18 % näher.
             * Bei 60 Bildern je Sekunde war das in einer knappen halben Sekunde
             * erledigt — nach dem Aufwachen aus dem Hintergrund drosselt iOS
             * die Bildrate aber, und derselbe Weg dauerte ein Vielfaches. Am
             * 16.08.2026 auf dem iPad als Zeitlupe beobachtet.
             */
            const anteil = annaeherungsanteil(abstandMs);
            eintrag.lat += (ziel.lat - eintrag.lat) * anteil;
            eintrag.lon += (ziel.lon - eintrag.lon) * anteil;
          }
          eintrag.marker.setLngLat([eintrag.lon, eintrag.lat]);
        }

        if (!berührt.current) {
          führen(karte, aktuell, markers.current, letzteAnpassung, letzterZoom, kamera, abstandMs);
        }
      }

      laufend = requestAnimationFrame(schritt);
    };

    laufend = requestAnimationFrame(schritt);
    return () => {
      cancelAnimationFrame(laufend);
    };
  }, []);

  return (
    <>
      <div className="karte" ref={container} />
      {ausserhalb && (
        <p className="kartenluecke">
          Für diesen Bereich liegt keine Karte vor. Die Kolonne wird trotzdem verfolgt.
        </p>
      )}
    </>
  );
}

/**
 * Kartenführung.
 *
 * `self` hält das eigene Fahrzeug in der Mitte, `all` passt den Ausschnitt so
 * an, dass die ganze Kolonne sichtbar bleibt — die Sicht des
 * Führungsfahrzeugs. `leader` rückt die Spitze in die Mitte und lässt die
 * eigene Position im Bild. `navigation` schiebt das eigene Fahrzeug nach unten
 * und gibt den Platz darüber der Strecke. `free` überlässt die Karte dem
 * Fahrer, sobald er sie selbst verschoben hat.
 */
function führen(
  karte: MapLibreMap,
  props: ConvoyMapProps,
  markers: Map<string, Rendered>,
  letzteAnpassung: { current: number },
  letzterZoom: { current: number | null },
  kamera: { current: Kamerastand },
  abstandMs: number,
): void {
  if (props.follow === 'free') {
    return;
  }

  /*
   * Die Vergrößerung wird einmal für die geltende Ansicht bestimmt, nicht in
   * jedem Zweig neu. `null` heißt: Diese Ansicht setzt keine — beim Einpassen
   * rechnet `fitBounds` sie aus den Grenzen.
   *
   * Die Rückschau (`letzterZoom`) wird von `self` und `navigation` geteilt.
   * Beim Wechsel zwischen beiden springt die Karte deshalb nicht: Sie rechnen
   * aus demselben vorigen Wert weiter.
   */
  const zoom = kartenZoom(props.follow, props.ownSpeedMps, letzterZoom.current);
  if (zoom !== null) {
    letzterZoom.current = zoom;
  }

  if (props.follow === 'navigation') {
    zeigeWeg(karte, props, markers, zoom, kamera, abstandMs);
    return;
  }

  if (props.follow === 'self') {
    const eigenes = props.youId === null ? undefined : markers.get(props.youId);

    if (eigenes === undefined) {
      // Die eigene Position ist noch nicht bekannt — beim ersten Start, ohne
      // Standortfreigabe oder im Funkloch. Statt auf einem beliebigen Ausschnitt
      // stehen zu bleiben, wird die Kolonne gezeigt: Sie ist das, worauf es
      // ankommt, und der Fahrer sieht wenigstens, wo die anderen sind.
      passeKolonneEin(karte, markers, letzteAnpassung, ausrichtungsgrad(props), props.bottomOverlayPx);
      return;
    }
    folgeDemFahrzeug(karte, props, eigenes, zoom, kamera, abstandMs);
    return;
  }

  if (props.follow === 'leader') {
    zeigeFührung(karte, props, markers, letzteAnpassung);
    return;
  }

  passeKolonneEin(karte, markers, letzteAnpassung, ausrichtungsgrad(props), props.bottomOverlayPx);
}

/**
 * Die Karte folgt dem eigenen Fahrzeug — ohne eigene Kamerafahrt.
 *
 * ## Warum `jumpTo` und nicht `easeTo`
 *
 * Die Symbolposition ist bereits Bild für Bild geglättet (`approachTauMs`).
 * Bis zum 03.09.2026 fuhr die Kamera darauf noch einmal weich zu — und weil
 * `easeTo` in jedem Bild neu begann, kam sie nie an: `_stop()` bricht die
 * laufende Fahrt ab, die neue beginnt bei null. Doppelte Glättung, bei der die
 * zweite nichts zu Ende bringt.
 *
 * Sichtbar wurde das erst, als die Ansicht am 02.09.2026 eine eigene
 * Vergrößerung bekam (A-93). Vorher erbte sie den weit herausgezoomten Wert der
 * Kolonnenansicht, und dort sind die 200 Meter, ab denen `weitWeg` springt,
 * ganze **vier Bildpunkte**. Bei Zoom 15,25 sind es **fünfundachtzig**: Am
 * 03.09.2026 früh wanderte das Symbol sichtbar aus der Mitte, bis die Karte
 * hinterhersprang. Aus dem Bildschirmfoto gerechnet waren es 290 Meter
 * Rückstand, rund sechzehn Sekunden bei 66 km/h.
 *
 * Mit `jumpTo` folgt die Kamera der geglätteten Zahl **genau**. Das Symbol
 * bleibt in der Mitte; die Weichheit kommt von dort, wo sie hingehört.
 *
 * ## Warum Vergrößerung und Ausrichtung trotzdem geglättet werden
 *
 * Beide ändern sich in Sprüngen: die Vergrößerung in Viertelstufen, der Kurs
 * nur mit jeder Meldung, also etwa einmal je Sekunde. Ohne Glättung ruckelte
 * die Karte an genau diesen Stellen. Sie wird deshalb hier geglättet, mit
 * derselben Zeitkonstante wie die Fahrzeuge — nicht von MapLibre, damit keine
 * Kamerafahrt entsteht, die wieder in jedem Bild abgebrochen wird.
 *
 * Beim ersten Bild gibt es nichts zu glätten: Dann gilt der Zielwert sofort.
 */
function folgeDemFahrzeug(
  karte: MapLibreMap,
  props: ConvoyMapProps,
  eigenes: Rendered,
  zielZoom: number | null,
  kamera: { current: Kamerastand },
  abstandMs: number,
): void {
  kamera.current = kameraSchritt(
    kamera.current,
    { zoom: zielZoom, bearing: ausrichtungsgrad(props) },
    abstandMs,
  );

  /*
   * Ein Sprung und keine Kamerafahrt: Das Ziel ist bereits geglättet — die
   * Symbolposition nähert sich Bild für Bild —, und eine zweite Glättung
   * darüber kommt nie an, weil jedes Bild `_stop()` aufruft (A-94).
   *
   * `easeTo` mit `duration: 0` statt `jumpTo`, aus demselben Grund wie in
   * `zeigeWeg`: Gebraucht wird der **Versatz**, und den kennt `jumpTo` nicht.
   * Bei `duration: 0` führt MapLibre das Bild unmittelbar aus (`_ease` ruft
   * `frame(1)`) und landet genau auf dem Ziel. Es bleibt ein Sprung.
   */
  karte.easeTo({
    center: [eigenes.lon, eigenes.lat],
    bearing: kamera.current.bearing ?? 0,
    ...(kamera.current.zoom === null ? {} : { zoom: kamera.current.zoom }),
    /*
     * Der Zielpunkt hängt an der Ausrichtung (A-105): nach Norden mittig, in
     * Fahrtrichtung ins untere Drittel, damit der Weg voraus Platz bekommt.
     *
     * Gemessen wird beides gegen den **sichtbaren** Streifen. Die Karte füllt
     * den ganzen Bildschirm, die Bedienleiste liegt darüber — ohne das lag das
     * eigene Fahrzeug im Querformat halb unter „Führungsfahrzeug zeigen"
     * (A-103).
     */
    offset: [
      0,
      followOffsetY(
        karte.getContainer().clientHeight,
        props.bottomOverlayPx,
        props.orientation === 'course',
      ),
    ],
    duration: 0,
  });
}

/**
 * Die Ansicht während einer laufenden Navigation.
 *
 * Beim Navigieren sucht der Fahrer etwas anderes als in der Kolonnenansicht:
 * nicht „wo sind die anderen", sondern „was kommt gleich". Sein Fahrzeug rückt
 * deshalb nach unten, und der Platz darüber gehört der Strecke — so wie es
 * jedes Navigationsgerät macht.
 *
 * Am 26.08.2026 hatte ein Fahrer die Kolonnenansicht an, während seine
 * Navigation lief. Die Kreuzung, an der er in 500 Metern abbiegen sollte, lag
 * außerhalb des Bildes.
 *
 * Die Vergrößerung folgt dem Tempo: Im Schritttempo die nächste Einmündung, auf
 * der Landstraße der nächste Kilometer. Gerechnet wird das in
 * `packages/shared` — hier wird es nur umgesetzt.
 */
function zeigeWeg(
  karte: MapLibreMap,
  props: ConvoyMapProps,
  markers: Map<string, Rendered>,
  zielZoom: number | null,
  kamera: { current: Kamerastand },
  abstandMs: number,
): void {
  const eigenes = props.youId === null ? undefined : markers.get(props.youId);
  if (eigenes === undefined) {
    // Ohne eigene Position gibt es keinen Weg nach vorn zu zeigen. Die Karte
    // bleibt, wo sie ist — etwas Falsches zu zeigen wäre schlechter.
    return;
  }

  kamera.current = kameraSchritt(
    kamera.current,
    { zoom: zielZoom, bearing: ausrichtungsgrad(props) },
    abstandMs,
  );

  /*
   * Hier `easeTo` mit `duration: 0` statt `jumpTo` — aus einem einzigen Grund:
   * Diese Ansicht braucht den **Versatz** nach unten, und den kennt `jumpTo`
   * nicht. Eine Kamerafahrt ist es trotzdem nicht: Bei `duration: 0` führt
   * MapLibre das Bild unmittelbar aus (`_ease` ruft `frame(1)` auf) und landet
   * genau auf dem Ziel. Es ist ein Sprung, der einen Versatz versteht.
   *
   * Bis zum 03.09.2026 stand hier `duration: 300`, und damit galt für diese
   * Ansicht derselbe Fehler wie für „mein Fahrzeug": Jedes Bild brach die
   * laufende Kamerafahrt ab und begann von vorn (A-94, A-95).
   */
  karte.easeTo({
    center: [eigenes.lon, eigenes.lat],
    bearing: kamera.current.bearing ?? 0,
    ...(kamera.current.zoom === null ? {} : { zoom: kamera.current.zoom }),
    // Nach unten aus der Mitte: Der Platz darüber gehört dem, was kommt.
    offset: [0, navigationOffsetY(karte.getContainer().clientHeight, props.bottomOverlayPx)],
    duration: 0,
  });
}

/**
 * Die Spitze der Kolonne in die Mitte — und die eigene Position bleibt im Bild.
 *
 * Wer zurückgefallen ist, sucht genau ein Fahrzeug: das vorderste. Bis zum
 * 16.08.2026 zeigte „Zum Führungsfahrzeug" dieselbe Ansicht wie beim
 * Führungsfahrzeug selbst — die ganze Kolonne eingepasst, mit der Führung
 * irgendwo klein am Rand. Die Beschriftung versprach mehr, als dahinterstand.
 *
 * Damit die Führung wirklich **mittig** steht und nicht nur mit im Bild ist,
 * wird der eigene Standort an ihr gespiegelt: Der Ausschnitt reicht dann nach
 * beiden Seiten gleich weit, und die eigene Position sitzt am Rand. Ein
 * schlichtes Einpassen der zwei Punkte rückte stattdessen die Mitte zwischen
 * beiden ins Zentrum — und damit wieder nicht die Spitze.
 *
 * Die Streckenführung dorthin kommt in Stufe 2; bis dahin wird sie nicht
 * vorgetäuscht.
 */
function zeigeFührung(
  karte: MapLibreMap,
  props: ConvoyMapProps,
  markers: Map<string, Rendered>,
  letzteAnpassung: { current: number },
): void {
  const führung = props.participants.find((p) => p.role === 'leader');
  const spitze = führung === undefined ? undefined : markers.get(führung.id);

  // Keine Führung auf der Karte — sie ist unsichtbar geschaltet oder hat noch
  // nichts gemeldet. Dann die Kolonne: Etwas Falsches zu zeigen wäre schlimmer
  // als etwas Weiteres.
  if (spitze === undefined) {
    passeKolonneEin(karte, markers, letzteAnpassung, ausrichtungsgrad(props), props.bottomOverlayPx);
    return;
  }

  const eigenes = props.youId === null ? undefined : markers.get(props.youId);

  if (eigenes === undefined) {
    /*
     * Ohne eigene Position gibt es nichts einzupassen; die Vergrößerung bleibt,
     * wie sie ist.
     *
     * Ein Sprung aus demselben Grund wie in `folgeDemFahrzeug`: Auch hier ist
     * das Ziel — die Symbolposition der Führung — bereits Bild für Bild
     * geglättet, und eine Kamerafahrt, die jedes Bild neu beginnt, kommt nie
     * an (A-95). Der Versatz holt sie aus der verdeckten Fläche (A-103).
     */
    karte.easeTo({
      center: [spitze.lon, spitze.lat],
      bearing: ausrichtungsgrad(props),
      offset: [0, centerOffsetY(karte.getContainer().clientHeight, props.bottomOverlayPx)],
      duration: 0,
    });
    return;
  }

  const jetzt = performance.now();
  if (jetzt - letzteAnpassung.current < MAP_FOLLOW.fitIntervalMs) {
    return;
  }
  letzteAnpassung.current = jetzt;

  const grenzen = new LngLatBounds();
  grenzen.extend([eigenes.lon, eigenes.lat]);
  grenzen.extend([2 * spitze.lon - eigenes.lon, 2 * spitze.lat - eigenes.lat]);

  einpassen(karte, grenzen, ausrichtungsgrad(props), props.bottomOverlayPx);
}

/**
 * Liegt das Ziel so weit vom jetzigen Ausschnitt entfernt, dass eine
 * Kamerafahrt dorthin unsinnig wäre?
 *
 * Dann wird gesprungen. Dieselbe Schwelle wie bei den Fahrzeugen: Was um
 * Kilometer springt, ist nicht gefahren worden, sondern kommt aus dem
 * Hintergrund oder aus einem Funkloch zurück.
 */
function weitWeg(karte: MapLibreMap, ziel: { lat: number; lon: number }): boolean {
  const mitte = karte.getCenter();
  return distanceM({ lat: mitte.lat, lon: mitte.lng }, ziel) > MAP_FOLLOW.jumpDistanceM;
}

/**
 * Wie die Karte stehen soll — nach Norden oder in Fahrtrichtung.
 *
 * Die eine Stelle, an der das entschieden wird. Vorher stand die Entscheidung
 * nur beim Nachführen des eigenen Fahrzeugs, und das Einpassen der Kolonne
 * kannte sie nicht: `fitBounds` ohne Angabe richtet nach Norden aus. Wer als
 * Führung „Ganze Kolonne zeigen" antippte, bekam die Karte gedreht, während die
 * Schaltfläche unverändert „Fahrtrichtung" anzeigte — die Anwendung tat etwas
 * anderes, als auf ihr stand.
 */
function ausrichtungsgrad(props: ConvoyMapProps): number {
  return props.orientation === 'course' ? kursDesEigenen(props) : 0;
}

/**
 * Passt den Ausschnitt so an, dass die ganze Kolonne sichtbar ist.
 *
 * Der Rand unten ist größer als oben: Dort liegen die Bedienleiste und die
 * Rückstandsanzeige über der Karte, und ein Fahrzeug darunter wäre so gut wie
 * nicht vorhanden.
 *
 * Anders als das Nachführen des eigenen Fahrzeugs geschieht das im eigenen
 * Takt und nicht in jedem Bild: `fitBounds` setzt auch die Vergrößerung, und
 * eine Kamerafahrt, die sechzig Mal je Sekunde von vorn beginnt, kommt nie an.
 */
function passeKolonneEin(
  karte: MapLibreMap,
  markers: Map<string, Rendered>,
  letzteAnpassung: { current: number },
  bearing: number,
  verdecktPx: number,
): void {
  const sichtbare = [...markers.values()];
  if (sichtbare.length === 0) {
    return;
  }

  const jetzt = performance.now();
  if (jetzt - letzteAnpassung.current < MAP_FOLLOW.fitIntervalMs) {
    return;
  }
  letzteAnpassung.current = jetzt;

  const grenzen = new LngLatBounds();
  for (const eintrag of sichtbare) {
    grenzen.extend([eintrag.lon, eintrag.lat]);
  }

  einpassen(karte, grenzen, bearing, verdecktPx);
}

/**
 * Einen Ausschnitt einpassen — mit Ausrichtung und mit Sprung bei großer
 * Entfernung.
 *
 * Die eine Stelle für beide Wege, die einpassen: die ganze Kolonne und die
 * Sicht auf die Führung. Ohne die Angabe der Ausrichtung richtete MapLibre
 * nach Norden aus und überschriebe damit die gewählte Ausrichtung; der
 * Ausschnitt wird im gedrehten Bild berechnet, alles Nötige bleibt also auch
 * in Fahrtrichtung vollständig sichtbar.
 */
function einpassen(
  karte: MapLibreMap,
  grenzen: LngLatBounds,
  bearing: number,
  verdecktPx: number,
): void {
  const mitte = grenzen.getCenter();
  const springen = weitWeg(karte, { lat: mitte.lat, lon: mitte.lng });

  /*
   * Unten wird die gemessene Höhe der Bedienleiste ausgespart, nicht mehr eine
   * feste Zahl.
   *
   * Bis zum 04.09.2026 standen hier 230 Bildpunkte. Das traf das Querformat
   * ungefähr und das Hochformat nicht: Dort ist die Leiste rund 245 hoch, bei
   * aufgeklappter Zustandsleiste deutlich mehr — und die Fahrzeuge am unteren
   * Rand des Ausschnitts lagen dann darunter. Gemessen wird die Höhe ohnehin
   * schon, für die Navigationsansicht (`bottomOverlayPx`).
   *
   * Der Zuschlag von 8 Bildpunkten hält die Symbole von der Kante frei; oben
   * und seitlich bleibt es bei den bewährten Werten. `fitBounds` verlangt, dass
   * die Polsterung kleiner ist als das Bild — sonst gibt es keinen Ausschnitt
   * mehr, in den etwas passt.
   */
  const hoehe = karte.getContainer().clientHeight;
  const unten = Math.max(0, Math.min(verdecktPx + 8, Math.max(0, hoehe - 90) - 1));

  karte.fitBounds(grenzen, {
    padding: { top: 90, bottom: unten, left: 60, right: 60 },
    maxZoom: 15,
    duration: springen ? 0 : MAP_FOLLOW.fitDurationMs,
    bearing,
  });
}

function kursDesEigenen(props: ConvoyMapProps): number {
  const eigenes = props.vehicles.find((f) => f.participantId === props.youId);
  return eigenes?.headingDeg ?? 0;
}
